import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, Snowflake, Loader2, MapPin } from 'lucide-react';
import { useI18n } from "@/i18n/I18nContext";

// variant="bar": en la barra verde superior, solo icono y grados; si no hay
// datos (sin permiso de ubicación), no ocupa sitio.
export default function WeatherWidget({ variant = "chip" }) {
  const { t } = useI18n();
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Try cache first
    try {
      const cached = sessionStorage.getItem('colygon_weather_cache');
      if (cached) {
        const { weather, ts } = JSON.parse(cached);
        // Use cache if less than 30 minutes old
        if (Date.now() - ts < 30 * 60 * 1000) {
          setWeather(weather);
          setLoading(false);
          return;
        }
      }
    } catch {}

    if (!navigator.geolocation) {
      setError(t("weather.no_geo", "Geolocalización no soportada"));
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Para el tiempo basta con ~1 km: no se envía la posición exacta.
          const latitude = position.coords.latitude.toFixed(2);
          const longitude = position.coords.longitude.toFixed(2);
          const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`);
          const data = await res.json();
          setWeather(data.current_weather);
          // Cache result
          try {
            sessionStorage.setItem('colygon_weather_cache', JSON.stringify({ weather: data.current_weather, ts: Date.now() }));
          } catch {}
        } catch (err) {
          setError(t("weather.error", "Error al obtener clima"));
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setError(t("weather.denied", "Ubicación denegada"));
        setLoading(false);
      },
      { timeout: 5000, maximumAge: 300000 } // 5s timeout, accept 5min old GPS
    );
  }, [t]);

  const bar = variant === "bar";
  if (bar && (loading || error || !weather)) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 bg-white/80 backdrop-blur-md text-slate-500 hover:text-colygon-dark hover:bg-colygon-light/30 text-xs font-bold px-3 py-2 rounded-xl border border-slate-200">
        <Loader2 className="w-4 h-4 animate-spin text-colygon" />
        <span className="hidden sm:inline">{t("weather.loading", "Clima...")}</span>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="flex items-center gap-2 bg-white/80 backdrop-blur-md text-slate-300 text-xs font-bold px-3 py-2 rounded-full shadow-lg border border-white/50" title={error}>
        <MapPin className="w-4 h-4" />
        <span className="hidden sm:inline">{t("weather.no_data", "Sin datos")}</span>
      </div>
    );
  }

  const { temperature, weathercode } = weather;
  
  let Icon = Cloud;
  if (weathercode === 0) Icon = Sun;
  else if (weathercode >= 1 && weathercode <= 3) Icon = Cloud;
  else if (weathercode >= 51 && weathercode <= 67) Icon = CloudRain;
  else if (weathercode >= 71 && weathercode <= 77) Icon = Snowflake;
  else if (weathercode >= 80 && weathercode <= 82) Icon = CloudRain;
  else if (weathercode >= 95) Icon = CloudRain; 

  if (bar) {
    return (
      // Por debajo de 360 px no cabe con el resto de la barra: se oculta
      <div className="hidden min-[360px]:inline-flex items-center gap-1 h-8 px-1 text-sm font-semibold text-white/90 tabular-nums"
        title={t("weather.current", "Clima actual en tu ubicación")} data-testid="navbar-weather">
        <Icon className="w-4 h-4 text-sky-200" aria-hidden="true" />
        <span>{Math.round(temperature)}°</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 bg-white/80 backdrop-blur-md text-slate-500 hover:text-colygon-dark hover:bg-colygon-light/30 text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 hover:bg-white transition-all cursor-default" title={t("weather.current", "Clima actual en tu ubicación")}>
      <Icon className="w-4 h-4 text-sky-500 drop-shadow-sm" />
      <span className="hidden sm:inline">{temperature}°C</span><span className="sm:hidden">{Math.round(temperature)}°</span>
    </div>
  );
}
